import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/** One thing that can go wrong with a piece of software, as someone reporting a problem sees it. */
export interface SoftwareProblemTypeOption {
  id: string;
  name: string;
}

/** As the technology team sees it in Settings. */
export interface SoftwareProblemType extends SoftwareProblemTypeOption {
  sortOrder: number;
  /** Offered on new tickets. One no longer offered keeps the tickets that chose it. */
  offered: boolean;
  ticketCount: number;
}

/** Software on the school's list, as anyone reporting a problem sees it. */
export interface SoftwareOption {
  id: string;
  name: string;
  publisher: string | null;
  /** What this software offers as the type of problem; empty means the device categories are used instead. */
  problemTypes: SoftwareProblemTypeOption[];
}

/** An entry as the technology team sees it in Settings. */
export interface SoftwareEntry extends Omit<SoftwareOption, 'problemTypes'> {
  notes: string | null;
  /** Offered when someone reports a problem. */
  offered: boolean;
  articleCount: number;
  ticketCount: number;
  problemTypes: SoftwareProblemType[];
  createdAt: string;
  updatedAt: string;
}

/** The whole software list, for the technology team. */
export const useSoftwareList = (enabled = true) =>
  useQuery({
    queryKey: ['software', 'all'],
    queryFn: () => api.get<SoftwareEntry[]>('/software'),
    enabled,
    staleTime: 60_000,
  });

/** The software people can report a problem with. */
export const useOfferedSoftware = (enabled = true) =>
  useQuery({
    queryKey: ['software', 'offered'],
    queryFn: () => api.get<SoftwareOption[]>('/software', { offered: true }),
    enabled,
    staleTime: 60_000,
  });
